<template>
  <section class="portal-home">
    <div class="portal-shell">
      <header class="portal-topbar">
        <div class="brand-group">
          <a href="/" class="portal-brand">
            <img class="brand-logo" src="@/assets/logo.png" alt="The Prevention Program" />
          </a>
        </div>
        <div class="portal-status">
          <span class="status-dot"></span>
          <span>Secure meeting portal</span>
          <i class="fa fa-shield" aria-hidden="true"></i>
        </div>
      </header>

      <div v-if="entryError" class="entry-error" role="alert">
        <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
        <div>
          <strong>Unable to open this meeting</strong>
          <span>{{ entryError }}</span>
        </div>
      </div>

      <main class="portal-main">
        <div class="portal-copy">
          <p class="portal-kicker"><span></span>The Prevention Program</p>
          <h1>Your secure meeting workspace<span>.</span></h1>
          <p class="portal-subtitle">Join your scheduled care session quickly and securely from one place.</p>

          <div class="access-panel">
            <div class="access-content">
              <span class="access-icon"><i class="fa fa-unlock-alt" aria-hidden="true"></i></span>
              <div class="access-copy">
                <div class="access-heading">
                  <strong>Secure session link required</strong>
                  <span class="encrypted-label">Encrypted</span>
                </div>
                <p>When your appointment is ready, use your secure meeting link to continue to camera and microphone setup.</p>
              </div>
            </div>
            <div class="access-footer">
              <span><i class="fa fa-check" aria-hidden="true"></i>Session token verified from care portal</span>
              <span class="help-label" title="Open your secure meeting link from the Prevention Program care portal.">Help <i class="fa fa-angle-right" aria-hidden="true"></i></span>
            </div>
          </div>

          <div class="trust-row">
            <div>
              <i class="fa fa-shield" aria-hidden="true"></i>
              <strong>Encrypted access</strong>
              <span>License-based<br />meeting protection</span>
            </div>
            <div>
              <i class="fa fa-bolt" aria-hidden="true"></i>
              <strong>Quick setup</strong>
              <span>Join your session in<br />seconds</span>
            </div>
            <div>
              <i class="fa fa-lock" aria-hidden="true"></i>
              <strong>Private by design</strong>
              <span>Secure care<br />communication</span>
            </div>
          </div>
        </div>

        <aside class="portal-preview" aria-label="Session device preview illustration">
          <div class="camera-preview">
            <!-- <img class="camera-preview-image" src="/assets/images/video-img.jpg" alt="Sample participant in the session preview" /> -->

            <div data-v-4109167e="" class="camera-off-state"><span data-v-4109167e="">GR</span><strong data-v-4109167e="">Gage Rodriguez</strong></div>
            <span class="camera-preview-name">You (Self View)</span>
            <div class="camera-preview-controls" aria-hidden="true">
              <span class="camera-preview-control" title="Microphone"><i class="fa fa-microphone"></i></span>
              <span class="camera-preview-control" title="Camera"><i class="fa fa-video-camera"></i></span>
              <span class="camera-preview-control camera-switch-control" title="Switch camera"><i class="fa fa-refresh"></i></span>
            </div>
          </div>

          <div class="preview-device-bar">
            <div class="preview-device">
              <span class="preview-device-icon"><i class="fa fa-microphone" aria-hidden="true"></i></span>
              <div class="preview-device-field">
                <label for="preview-microphone">Microphone</label>
                <div class="preview-device-select">
                  <select id="preview-microphone" disabled title="Device selection is available during meeting setup">
                    <option>Default microphone</option>
                  </select>
                  <i class="fa fa-angle-down" aria-hidden="true"></i>
                </div>
              </div>
            </div>
            <div class="preview-device">
              <span class="preview-device-icon"><i class="fa fa-volume-up" aria-hidden="true"></i></span>
              <div class="preview-device-field">
                <label for="preview-speaker">Speaker</label>
                <div class="preview-device-select">
                  <select id="preview-speaker" disabled title="Device selection is available during meeting setup">
                    <option>Default speaker</option>
                  </select>
                  <i class="fa fa-angle-down" aria-hidden="true"></i>
                </div>
              </div>
            </div>
            <div class="preview-device">
              <span class="preview-device-icon"><i class="fa fa-video-camera" aria-hidden="true"></i></span>
              <div class="preview-device-field">
                <label for="preview-camera">Camera</label>
                <div class="preview-device-select">
                  <select id="preview-camera" disabled title="Device selection is available during meeting setup">
                    <option>Default camera</option>
                  </select>
                  <i class="fa fa-angle-down" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </main>

      <footer class="portal-footer">
        <p>&copy; 2026 The Prevention Program. All rights reserved. </p>
        <div class="footer-labels">
          <a href="#" class="text-white">Privacy Policy</a>
          <a href="#"  class="text-white">Terms of Service</a></div>
      </footer>
    </div>
  </section>
</template>
<script>
import { mapActions } from "vuex";

export default {
  name: "Home-Page",
  data() {
    return {
      entryError: "",
    };
  },
  mounted() {
    this.$store.dispatch("IsLoading", { isLoading: false, isLoadingText: "" });
    this.handleQueryParamEntry();
  },
  methods: {
    ...mapActions(["setUser"]),

    getQueryParamValue(key) {
      const value = this.$route.query[key];
      return Array.isArray(value) ? value[0] : value;
    },

    getQueryParamText(key) {
      const value = this.getQueryParamValue(key);
      return typeof value === "string" ? value.trim() : "";
    },

    getQueryParamBoolean(key) {
      const value = this.getQueryParamText(key).toLowerCase();
      return value === "true" || value === "1" || value === "yes";
    },

    getQueryEntryMode() {
      return this.getQueryParamText("role").toLowerCase();
    },

    setEntryValidationError(roleLabel, missingFields) {
      const fieldList = missingFields.join(", ");
      this.entryError = `${roleLabel} meeting link is missing: ${fieldList}. Please open the meeting again from the Prevention Program portal.`;
      this.$toasted.error(this.entryError, { duration: 5000 });
    },

    openPreview(meetingCode, appointmentId = null) {
      const previewRoute = this.$router.resolve({
        path: `/preview/${meetingCode}`,
        query: appointmentId
          ? { appointmentId }
          : {},
      });

      // A document navigation prevents an already-open portal tab from
      // continuing with a stale, pre-deployment Preview chunk.
      window.location.replace(previewRoute.href);
    },

    async handleQueryParamEntry() {
      const role = this.getQueryEntryMode();
      if (!role) {
        if (Object.keys(this.$route.query).length > 0) {
          this.setEntryValidationError("Meeting", ["role"]);
        }
        return;
      }

      this.entryError = "";

      if (role === "coach" || role === "host") {
        await this.startMeetingFromQueryParams();
        return;
      }

      if (role === "participant" || role === "join") {
        await this.joinMeetingFromQueryParams();
        return;
      }

      this.entryError = "This meeting link contains an unsupported role. Please open the meeting again from the Prevention Program portal.";
      this.$toasted.error(this.entryError, { duration: 5000 });
    },

    async startMeetingFromQueryParams() {
      const appointmentId =
        this.getQueryParamText("appointmentId") || this.getQueryParamText("appointment_id");
      const meetingCode = this.getQueryParamText("meetingCode");
      const userId = this.getQueryParamText("user_id") || this.getQueryParamText("userId");
      const token = this.getQueryParamText("token") || this.getQueryParamText("accessToken");
      const hostName = this.getQueryParamText("hostName");
      const hostEmail = this.getQueryParamText("hostEmail");
      const meetingName = this.getQueryParamText("meetingName");

      const missingFields = [
        [appointmentId, "appointment ID"],
        [userId, "user ID"],
        [token, "access token"],
        [hostName, "coach name"],
        [hostEmail, "coach email"],
        [meetingCode || meetingName, "meeting code"],
      ]
        .filter(([value]) => !value)
        .map(([, label]) => label);

      if (missingFields.length) {
        this.setEntryValidationError("Coach", missingFields);
        return;
      }

      if (meetingCode) {
        await this.joinMeeting({
          appointmentId,
          meetingCode,
          userId,
          token,
          userName: hostName,
          userEmail: hostEmail,
          meetingPassword: this.getQueryParamText("meetingPassword"),
        });
        return;
      }

      // Backward-compatible fallback for older links. New appointment links
      // always include the automatically provisioned meeting code.
      await this.startNewMeeting({
        appointmentId,
        userId,
        token,
        hostName,
        hostEmail,
        meetingName,
        meetingPassword: this.getQueryParamText("meetingPassword"),
        isPasswordEnabled: this.getQueryParamBoolean("isPasswordEnabled"),
      });
    },

    async joinMeetingFromQueryParams() {
      const meetingCode = this.getQueryParamText("meetingCode");
      const userId = this.getQueryParamText("user_id") || this.getQueryParamText("userId");
      const token = this.getQueryParamText("token") || this.getQueryParamText("accessToken");
      const userName = this.getQueryParamText("userName");
      const userEmail = this.getQueryParamText("userEmail");

      const missingFields = [
        [meetingCode, "meeting code"],
        [userId, "user ID"],
        [token, "access token"],
        [userName, "participant name"],
        [userEmail, "participant email"],
      ]
        .filter(([value]) => !value)
        .map(([, label]) => label);

      if (missingFields.length) {
        this.setEntryValidationError("Participant", missingFields);
        return;
      }

      await this.joinMeeting({
        meetingCode,
        userId,
        token,
        userName,
        userEmail,
        meetingPassword: this.getQueryParamText("meetingPassword"),
      });
    },

    async startNewMeeting(meetingDetails) {
      try {
        this.$store.dispatch("IsLoading", {
          isLoading: true,
          isLoadingText: "Creating Room..."
        });

        const meetingResponse = await this.$goigi.CreateNewMeetingFromPortal({
          appointmentId: meetingDetails.appointmentId,
          userId: meetingDetails.userId,
          token: meetingDetails.token,
          hostName: meetingDetails.hostName,
          hostEmail: meetingDetails.hostEmail,
          meetingName: meetingDetails.meetingName,
          meetingPassword: meetingDetails.meetingPassword,
          isPasswordEnabled: meetingDetails.isPasswordEnabled,
          meetingType: "OTM"
        });

        if (meetingResponse.status === 0) {
          console.error("Error:", meetingResponse.message);
          this.$store.dispatch("IsLoading", { isLoading: false, isLoadingText: "" });
          this.$toasted.error(
            meetingResponse.message || "Failed to create a meeting. Please try again.",
            { duration: 2000 }
          );
          return;
        }

        if (meetingResponse.status === 1 && meetingResponse.meetingCode) {
          this.$store.dispatch("IsLoading", { isLoading: false, isLoadingText: "" });

          const goigiStore = this.$goigi.getMeetingStore();
          this.setUser({ ...goigiStore.user });

          this.openPreview(
            meetingResponse.meetingCode,
            meetingDetails.appointmentId,
          );
        } else {
          console.error("Meeting code not returned.");
          this.$store.dispatch("IsLoading", { isLoading: false, isLoadingText: "" });
          this.$toasted.error("Unable to create meeting. Please try again.", { duration: 2000 });
        }
      } catch (e) {
        console.error("startNewMeeting Error:", e);
        this.$store.dispatch("IsLoading", { isLoading: false, isLoadingText: "" });
        this.$toasted.error(e.message || "Failed to create a meeting. Please try again.", { duration: 2000 });
      }
    },

    async joinMeeting(meetingDetails) {
      try {
        this.$store.dispatch("IsLoading", {
          isLoading: true,
          isLoadingText: "Joining..."
        });

        const meeting = await this.$goigi.JoinMeeting({
          meetingCode: meetingDetails.meetingCode,
          userId: meetingDetails.userId,
          userName: meetingDetails.userName,
          userEmail: meetingDetails.userEmail,
          meetingPassword: meetingDetails.meetingPassword,
          token: meetingDetails.token
        });

        this.$store.dispatch("IsLoading", {
          isLoading: false,
          isLoadingText: ""
        });

        const goigiStore = this.$goigi.getMeetingStore();
        this.setUser({ ...goigiStore.user });

        if (
          meeting.status === 1 &&
          meeting.meetingCode &&
          !meeting.endedAt
        ) {
          this.openPreview(meetingDetails.meetingCode, meeting.appointmentId);
        } else {
          console.error("Invalid meetingCode!");
          this.$store.dispatch("IsLoading", { isLoading: false, isLoadingText: "" });
          this.$toasted.error("Invalid meetingCode! Please try again.", { duration: 2000 });
        }
      } catch (e) {
        console.error(e);
        this.$store.dispatch("IsLoading", {
          isLoading: false,
          isLoadingText: ""
        });
        this.$toasted.error(e.message, { duration: 2000 });
      }
    }
  }
};
</script>

<style scoped>
.camera-off-state {
    align-items: center;
       background: #0a525a;
    background: radial-gradient(circle, #037d89, #0a525a);
    bottom: 0;
    color: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 14px;
    justify-content: center;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 3;
}
.camera-off-state span {
    align-items: center;
    background: #0a464c;
    border-radius: 50%;
    display: flex;
    font-size: 26px;
    font-weight: 700;
    height: 92px;
    justify-content: center;
    width: 92px;
}
.portal-home {
  min-height: 100vh;
  padding: 15px 56px 24px;
  color: #f4ffff;
  background:
    radial-gradient(ellipse at 0% 18%, rgba(15, 126, 130, 0.5), transparent 48%),
    radial-gradient(ellipse at 95% 76%, rgba(20, 124, 122, 0.5), transparent 42%),
    #052b35;
}
.portal-home, .portal-home * { box-sizing: border-box; }
.portal-shell { max-width: 1400px; min-height: calc(100vh - 56px); margin: 0 auto; display: flex; flex-direction: column; }
.portal-topbar, .brand-group, .portal-brand, .portal-status { display: flex; align-items: center; }
.portal-topbar { justify-content: space-between; gap: 24px; }
.brand-group { gap: 12px; }
.portal-brand { background: #fff; color: #003e50; border-radius: 10px; padding: 11px 17px; gap: 12px; min-height: 54px; text-decoration: none; }
.brand-logo { display: block; width: 150px; max-width: 100%; height: auto; }
.brand-icon { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 8px; background: #16b8af; color: #fff; font-size: 22px; }
.brand-name { display: flex; flex-direction: column; line-height: 1.15; }
.brand-name small { color: #667a99; font-size: 11px; font-weight: 700; }
.brand-name strong { font-size: 14px; white-space: nowrap; }
.workspace-label { color: #24d3c3; background: rgba(116, 201, 203, 0.15); border: 1px solid rgba(45, 211, 196, 0.2); border-radius: 20px; padding: 4px 10px; font-size: 13px; }
.portal-status { gap: 10px; background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 24px; padding: 8px 16px; font-size: 15px; }
.status-dot { display: inline-block; flex: 0 0 auto; width: 8px; height: 8px; border-radius: 50%; background: #10bd89; }
.portal-status > .status-dot { width: 10px; height: 10px; background: #20c76a; }
.portal-status > i { color: #25cebf; border-left: 1px solid #45606a; padding-left: 13px; margin-left: 3px; font-size: 12px; }
.entry-error { display: flex; align-items: flex-start; gap: 12px; background: #7f1d1d; border: 1px solid #fca5a5; border-radius: 8px; padding: 16px 18px; margin-top: 24px; }
.entry-error strong, .entry-error span { display: block; }
.entry-error span { margin-top: 4px; }
.portal-main { display: grid; grid-template-columns: minmax(0, 454px) minmax(0, 1fr); gap: 56px; align-items: center; width: 100%; margin: auto 0; padding: 50px 0 60px; }
.portal-kicker { display: inline-flex; align-items: center; gap: 8px; padding: 4px 12px; margin: 0 0 14px; color: #2cdbcb; border: 1px solid rgba(25, 196, 188, 0.2); background: rgba(15, 161, 159, 0.16); border-radius: 6px; text-transform: uppercase; font-size: 12px; font-weight:600; letter-spacing: 1px;}
.portal-kicker > span { width: 6px; height: 6px; border-radius: 50%; background: #2bd1bf; }
.portal-copy h1 { font-size: 56px; line-height: 0.94; font-weight: 800; letter-spacing: 1px; margin: 0 0 18px; }
.portal-copy h1 > span { color: #a1e9e3; }
.portal-subtitle { font-size: 19px; line-height: 1.5; color: #bdd4dd; margin: 0 0 24px; }
.access-panel { background: rgba(194, 227, 236, 0.09); border: 1px solid rgba(191, 226, 237, 0.19); border-radius: 20px; padding: 24px; margin-bottom: 28px; }
.access-content { display: flex; align-items: flex-start; gap: 16px; }
.access-icon { display: grid; place-items: center; flex: 0 0 48px; height: 48px; background: rgba(17, 191, 183, 0.23); border: 1px solid rgba(38, 218, 204, 0.38); color: #2cd3c3; border-radius: 12px; font-size: 23px; }
.access-copy { flex: 1; min-width: 0; }
.access-heading { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px; margin-bottom: 7px; }
.access-heading strong { font-size: 16px; }
.encrypted-label { color: #27d1c0; background: rgba(16, 183, 177, 0.22); border: 1px solid rgba(16, 183, 177, 0.24); border-radius: 16px; padding: 2px 8px; font-size: 12px; }
.access-copy p { color: #bad0db; font-size: 15px; line-height: 1.35; margin: 0; }
.access-footer { border-top: 1px solid rgba(179, 215, 229, 0.14); padding-top: 16px; margin-top: 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; font-size: 12px; color: #b9cfd8; }
.access-footer i { color: #25d2bd; margin-right: 9px; }
.help-label { color: #24d5c6; white-space: nowrap; }
.help-label i { margin: 0 0 0 7px; }
.trust-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.trust-row > div { background: rgba(182, 217, 236, 0.09); border: 1px solid rgba(191, 226, 237, 0.17); border-radius: 20px; padding: 16px; }
.trust-row i { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 8px; background: rgba(188, 221, 234, 0.13); color: #20cbbc; font-size: 16px; margin-bottom: 10px; }
.trust-row strong, .trust-row span { display: block; font-size: 12px; line-height: 1.3; }
.trust-row strong { margin-bottom: 3px; }
.trust-row span { color: #b9cfd8; }
.portal-preview { min-width: 0; }
.session-progress { display: flex; align-items: center; list-style: none; padding: 0 8px; margin: 0 0 16px; gap: 12px; color: #92aebb; font-size: 13px; }
.session-progress li { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.session-progress li:not(:last-child) { flex: 1; }
.session-progress li:not(:last-child)::after { content: ""; height: 1px; background: rgba(179, 215, 229, 0.17); flex: 1; margin-left: 4px; min-width: 12px; }
.session-progress li > span { display: grid; place-items: center; width: 20px; height: 20px; flex: 0 0 20px; border-radius: 50%; background: #214a55; color: #dbeef0; font-size: 11px; }
.session-progress .current-step { color: #fff; font-weight: 700; }
.session-progress .current-step > span { color: #064b50; background: #27cdba; box-shadow: 0 0 0 4px rgba(39, 205, 186, 0.2); }
.meeting-window { border: 1px solid rgba(255, 255, 255, 0.8); border-radius: 24px; background: #f7f9fc; overflow: hidden; color: #23314a; }
.window-header { min-height: 51px; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.window-dots { display: flex; gap: 8px; }
.window-dots span { width: 12px; height: 12px; border-radius: 50%; background: #fc5056; }
.window-dots span:nth-child(2) { background: #ffa51b; }
.window-dots span:nth-child(3) { background: #20bd96; }
.room-label { display: inline-flex; align-items: center; gap: 10px; border: 1px solid #dfe7f2; background: #fff; border-radius: 20px; padding: 3px 13px; font-size: 13px; color: #485a74; box-shadow: 0 1px 2px #e6eaf0; }
.room-label i { color: #029d9e; }
.connection-quality { display: flex; align-items: center; gap: 8px; color: #0dbb85; }
.connection-quality span { font-size: 10px; font-weight: 700; color: #41516a; background: #e8edf5; padding: 3px 5px; border-radius: 4px; }
.video-stage { position: relative; min-height: 370px; background: radial-gradient(ellipse at 37% 30%, #107984, #06434e 70%, #06353e); display: flex; flex-direction: column; padding: 24px; color: #f4ffff; }
.stage-topline, .stage-bottomline { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.consultation-label { display: inline-flex; align-items: center; gap: 8px; background: rgba(0, 41, 49, 0.45); border-radius: 10px; padding: 5px 12px; font-size: 12px; }
.consultation-label .status-dot { background: #31d4a5; }
.compliance-label { font-size: 11px; color: #a7cfd4; }
.preview-identity { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; padding: 26px 0; gap: 13px; }
.tile-avatar { display: grid; place-items: center; position: relative; width: 112px; height: 112px; border: 3px solid #20b9b0; border-radius: 50%; background: rgba(9, 101, 111, 0.5); font-size: 38px; font-weight: 800; }
.tile-avatar > span { display: grid; place-items: center; position: absolute; bottom: 1px; right: 1px; width: 24px; height: 24px; border: 4px solid #fff; border-radius: 50%; background: #0fbd8b; font-size: 9px; }
.preview-identity > strong { font-size: 15px; font-weight: 500; }
.self-label, .host-label { background: rgba(0, 26, 33, 0.65); border-radius: 10px; padding: 7px 12px; font-size: 12px; }
.host-label { text-align: center; padding: 7px 14px; }
.host-label strong, .host-label small { display: block; line-height: 1.35; }
.host-label small { color: #8cddd2; font-size: 11px; }
.host-label .status-dot { width: 6px; height: 6px; margin-right: 4px; background: #28cebd; }
.device-panel { padding: 20px; }
.device-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.device-tile { display: flex; align-items: center; gap: 10px; border: 1px solid #dfe7f2; border-radius: 12px; background: #fff; padding: 12px; box-shadow: 0 1px 2px #e6eaf0; }
.device-tile > i { display: grid; place-items: center; width: 32px; height: 32px; flex: 0 0 32px; color: #009c9b; background: #effcf9; border-radius: 8px; font-size: 15px; }
.device-tile > div { flex: 1; min-width: 0; }
.device-tile strong, .device-tile small { display: block; font-size: 12px; line-height: 1.3; }
.device-tile small { color: #7287a8; }
.meeting-controls { display: flex; justify-content: center; gap: 12px; padding-top: 24px; }
.meeting-controls > span { display: grid; place-items: center; width: 48px; height: 48px; border: 1px solid #dee6f2; border-radius: 50%; color: #394a63; background: #fff; font-size: 18px; box-shadow: 0 1px 2px #e6eaf0; }
.meeting-controls > .end-control { background: #fa4048; color: #fff; border-color: #fa4048; box-shadow: 0 5px 9px rgba(250, 64, 72, 0.2); }
.end-control i { transform: rotate(135deg); }
.privacy-note { display: flex; align-items: flex-start; justify-content: center; gap: 8px; margin: 16px 0 0; color: #83a7b3; font-size: 12px; line-height: 1.5; text-align: center; }
.privacy-note i { color: #16cabb; margin-top: 3px; }
.portal-footer { display: flex; align-items: center; justify-content: space-between; gap: 24px; border-top: 1px solid rgba(165, 206, 220, 0.13); padding-top: 24px; color: #85a4b4; font-size: 12px; }
.portal-footer p { margin: 0; font-size: 12px; }
.footer-divider { padding: 0 10px; }
.footer-labels { display: flex; gap: 24px; }

@media (min-width: 1440px) {
  .portal-main { grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.25fr); }
  .portal-copy { max-width: 500px; }
}
@media (max-width: 1100px) {
  .portal-home { padding: 28px; }
  .portal-main { grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr); gap: 28px; }
  .portal-copy h1 { font-size: 50px; }
  .access-panel { padding: 18px; }
  .trust-row > div { padding: 12px; }
  .window-header { padding: 12px; }
  .window-dots { gap: 5px; }
  .room-label { font-size: 11px; padding: 3px 8px; }
  .device-panel { padding: 16px 12px; }
  .device-tile { gap: 6px; padding: 10px 7px; }
  .device-tile > i { flex-basis: 26px; width: 26px; height: 28px; }
}
@media (max-width: 900px) {
  .portal-main { grid-template-columns: 1fr; gap: 40px; padding: 48px 0; max-width: 660px; margin: auto; }
  .portal-copy h1 { font-size: 60px; }
  .portal-footer { flex-wrap: wrap; }
  .workspace-label { display: none; }
  .device-tile { padding: 12px; gap: 10px; }
}
@media (max-width: 575px) {
  .portal-home { padding: 20px 18px; }
  .portal-shell { min-height: calc(100vh - 40px); }
  .portal-topbar { flex-wrap: wrap; gap: 14px; }
  .portal-brand { border-radius: 12px; }
  .portal-status { font-size: 13px; }
  .portal-main { padding: 36px 0; gap: 32px; }
  .portal-copy h1 { font-size: 48px; }
  .portal-subtitle { font-size: 17px; }
  .access-content { gap: 12px; }
  .access-panel { padding: 18px; border-radius: 16px; }
  .access-heading strong { font-size: 14px; }
  .access-copy p { font-size: 14px; }
  .access-footer { align-items: flex-start; }
  .access-footer > span:first-child { display: flex; align-items: baseline; }
  .trust-row { gap: 8px; }
  .trust-row > div { padding: 12px 10px; border-radius: 12px; }
  .trust-row strong, .trust-row span { font-size: 11px; }
  .session-progress { padding: 0; gap: 8px; font-size: 11px; }
  .session-progress li { gap: 5px; }
  .session-progress li:not(:last-child)::after { margin-left: 0; min-width: 4px; }
  .meeting-window { border-radius: 18px; }
  .window-header { gap: 8px; }
  .window-dots span { width: 8px; height: 8px; }
  .room-label { font-size: 10px; gap: 5px; padding: 3px 6px; }
  .connection-quality { gap: 4px; font-size: 12px; }
  .video-stage { min-height: 320px; padding: 16px; }
  .stage-topline { flex-wrap: wrap; gap: 8px; }
  .consultation-label { font-size: 11px; padding: 5px 8px; }
  .compliance-label { font-size: 10px; }
  .device-grid { gap: 7px; }
  .device-tile { flex-wrap: wrap; padding: 10px 7px; gap: 7px; }
  .device-tile > div { flex-basis: 100%; order: 2; }
  .device-tile > .status-dot { margin-left: auto; }
  .device-tile strong, .device-tile small { font-size: 11px; }
  .meeting-controls { padding-top: 20px; }
  .portal-footer { gap: 16px; padding-top: 20px; line-height: 1.6; }
  .footer-labels { flex-wrap: wrap; gap: 12px 20px; }
}
.portal-preview {
    padding: 12px;
    background: #ffffff47;
    color: #263238;
    border-radius: 20px;
}
.camera-preview {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 18px;
  background: #dce4e1;
}
.camera-preview-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.camera-preview-name {
  position: absolute;
  left: 16px;
  bottom: 22px;
  max-width: calc(50% - 100px);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  text-shadow: 0 1px 4px #303030;
  overflow-wrap: anywhere;
}
.camera-preview-controls {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 9;
}
.camera-preview-control {
  display: grid;
  place-items: center;
  flex: 0 0 50px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #f3f6f5;
  color: #202423;
  font-size: 21px;
}
.camera-switch-control {
  color: #fff;
  background: rgba(32, 36, 35, 0.75);
}
.preview-device-bar {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 18px;
  padding: 22px 12px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 3px 12px rgba(27, 53, 42, 0.07);
}
.preview-device {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 0 12px;
}
.preview-device + .preview-device { border-left: 1px solid #dce2e7; }
.preview-device-icon {
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #d5eae4;
  color: #00bd8e;
  font-size: 17px;
}
.preview-device-field { flex: 1; min-width: 0; }
.preview-device-field label {
  display: block;
  margin: 0 0 4px;
  color: #93a5c2;
  font-size: 12px;
  line-height: 1.25;
}
.preview-device-select { position: relative; }
.preview-device-select select {
  display: block;
  appearance: none;
  width: 100%;
  padding: 0 18px 0 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #263238;
  -webkit-text-fill-color: #263238;
  opacity: 1;
  font: inherit;
  font-size: 12px;
  line-height: 1.5;
  text-overflow: ellipsis;
}
.preview-device-select > i {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: #46514f;
  pointer-events: none;
}
@media (max-width: 1100px) and (min-width: 901px) {
  .preview-device-bar { padding: 18px 4px; }
  .preview-device { gap: 6px; padding: 0 6px; }
  .preview-device-icon { flex-basis: 28px; width: 28px; height: 28px; font-size: 14px; }
  .preview-device-select select { font-size: 11px; }
}
@media (max-width: 575px) {
  .portal-preview { padding: 10px; }
  .camera-preview { border-radius: 12px; }
  .camera-preview-controls { gap: 8px; bottom: 12px; }
  .camera-preview-control { flex-basis: 40px; width: 40px; height: 40px; font-size: 17px; }
  .camera-preview-name { left: 12px; bottom: 66px; max-width: calc(100% - 24px); font-size: 12px; }
  .preview-device-bar { grid-template-columns: 1fr; margin-top: 14px; padding: 6px 16px; border-radius: 12px; }
  .preview-device { padding: 12px 0; gap: 14px; }
  .preview-device + .preview-device { border-left: 0; border-top: 1px solid #e5ebe8; }
  .preview-device-field label { font-size: 12px; }
  .preview-device-select select { font-size: 14px; }
}
</style>
